import { authenticatedFetch } from "@lumi/data";
import { useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";

/**
 * Custom hook to manage approval and rejection workflow state
 * Integrates with /v1/admin/submissions/:id/approve and /reject endpoints
 */
export function useReviewWorkflow(submissionId: string | undefined) {
  const { accessToken, refreshAuth } = useAuth();
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [rejectionLoading, setRejectionLoading] = useState(false);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:4000";

  const handleApprove = () => {
    setShowApprovalDialog(true);
  };

  const handleApprovalConfirm = async (
    notes: string,
    publishImmediately: boolean,
    transferSubtitles: boolean = false,
  ) => {
    if (!submissionId || !accessToken) return;

    setApprovalLoading(true);
    try {
      await authenticatedFetch<{ ok: boolean }>(
        `${API_BASE}/v1/admin/submissions/${submissionId}/approve`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ notes, publishImmediately, transferSubtitles }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setShowApprovalDialog(false);

      const subtitleMsg = transferSubtitles ? " Subtitles will be transferred." : "";
      alert(
        `Submission approved! ${publishImmediately ? "Publishing immediately." : "Will publish later."}${subtitleMsg}`,
      );
      // Trigger page reload to show updated status
      globalThis.location.reload();
    } catch (error) {
      console.error("Approval failed:", error);
      alert(`Failed to approve: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleApprovalCancel = () => {
    setShowApprovalDialog(false);
  };

  const handleReject = () => {
    setShowRejectionDialog(true);
  };

  const handleRejectionConfirm = async (reason: string, notes: string) => {
    if (!submissionId || !accessToken) return;

    setRejectionLoading(true);
    try {
      await authenticatedFetch<{ ok: boolean }>(
        `${API_BASE}/v1/admin/submissions/${submissionId}/reject`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason, notes }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setShowRejectionDialog(false);
      alert(`Submission rejected. Reason: ${reason}`);
      // Trigger page reload to show updated status
      globalThis.location.reload();
    } catch (error) {
      console.error("Rejection failed:", error);
      alert(`Failed to reject: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setRejectionLoading(false);
    }
  };

  const handleRejectionCancel = () => {
    setShowRejectionDialog(false);
  };

  return {
    // Dialog state
    showApprovalDialog,
    showRejectionDialog,
    approvalLoading,
    rejectionLoading,
    // Handlers
    handleApprove,
    handleApprovalConfirm,
    handleApprovalCancel,
    handleReject,
    handleRejectionConfirm,
    handleRejectionCancel,
  };
}
